import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useReducedMotion } from 'motion/react';
import { cn } from '@/lib/utils';
import CycleRewind from './CycleRewind';
import { partLoaders, useParts } from './lazyParts';
import { storyAt, storyPages } from './story';
import StoryWindow from './StoryWindow';
import { REST_MS } from './tempo';
import useDip, { DIP_IN_SKIP_MS, DIP_OUT_MS } from './useDip';
import useMotionControls from './useMotionControls';
import usePlayback from './usePlayback';
import VignetteScope from './VignetteScope';

/*
 * A story of units (see story.js) played in an app window: the pages the
 * units play on, the hand that plays them (the director: the pointer and
 * each page's cues), and the playback that moves them.
 *
 *   const state = useStory(chain([COMPLETE, RELEASE, COMPOSE]), { pages: HERO_PAGES, parts: { drawer } });
 *   <StoryVignette state={state} hero={useHeroData()} trails={...} baseWidth={1200} height={790} />
 *
 * The pages are the caller's: each unit names the page it plays on, and
 * `pages` says what each page is:
 *
 *   { Component }            the page, in the page's first load, or
 *   { part: loader }         the page as a part (its module's default export),
 *   { cues: loader }         optionally, a part whose default export the
 *                            director renders for that page (its camera,
 *                            its pointer moves; a page that is a part may
 *                            carry its cues itself).
 *
 * `useStory` is separate so a page can hand the same story to a companion
 * (the hero's phone follows it) and so a unit can play alone:
 * `useStory(chain([COMPLETE]), { pages })`, or `chain([RELEASE], { settled:
 * [COMPLETE] })` for a unit that opens where another ended. A parent that
 * needs a unit back at its opening on demand (the Platform stepper starting
 * a step over) remounts the component that calls useStory (a new `key`): the
 * playback starts at step 0, cycle 0, and every part already loaded is
 * there from the first render. `active: false` holds it (and asks for no
 * part) while it is not the step on show.
 */

/**
 * The parts every story shares, by name: the story's hand (see
 * storyParts.js; a story's own parts are its own, e.g. HERO_PART_LOADERS).
 */
export const PART_LOADERS = partLoaders(() => import('./storyParts'), ['director']);

/**
 * How a story plays (usePlayback options): it starts once a fifth of it is
 * on screen (a hero story is long and loops, so it starts early rather than
 * finish before it is reached: on a 1280x830 or 1366x768 laptop the hero's
 * window shows under a third at the page's top, R34), keeps going until it
 * is entirely off screen, rests on its end (REST_MS, once what its last
 * beat set off has settled), then dips back to its opening and plays again.
 */
export const STORY_PLAYBACK = Object.freeze({ loop: true, rest: REST_MS, threshold: 0.2, pauseThreshold: 0 });

/** How long a page takes to come in once the one left is dropped (DIP_SWAP_MS into its DIP_OUT_MS fade): a window navigation is both (SETTLES.page). */
export const PAGE_IN_MS = 250;

/** How far ahead of its first beat a page is mounted (hidden), so it is laid out when the window turns to it. */
const MOUNT_AHEAD = 4;

const cuesPart = (page) => `${page}:cues`;
const NO_PAGES = Object.freeze({});

/**
 * Plays `story`: `{ ref, step, cycle, reduced, at, page, parts, pages,
 * story, rest }`. `ref` goes on the vignette (usePlayback). `at` is storyAt() for
 * the step; `page` the page the window shows (under reduced motion,
 * `reducedPage` if given, else the page its last beat shows).
 *
 * The parts: the director and the pages' cues are asked for once the page
 * has settled after loading (see lazyParts); then each part the beats need
 * (a beat's `needs`, a page that is a part), in the order the story first
 * needs them, while the story is in view: the first once the director and
 * the cues are in, each other once the one before it has arrived. (In view,
 * not playing: a story whose first beat needs a part cannot play before it
 * arrives.) The playback holds until the director and the cues are in, and
 * before a beat whose part has not arrived. Under reduced motion only the
 * resting page's part is asked for. `pages` defaults to none (a story on
 * pages that are all parts of `parts`); `parts` names the story's other
 * parts (`{ name: loader }`, e.g. a drawer's editors); `director` replaces
 * the director's loader. `rest` replaces the rest on its end (STORY_PLAYBACK's,
 * plus what the story's last beat `settles`), `threshold` STORY_PLAYBACK's.
 * `loop: false` plays the story once and holds its end (the Platform
 * stepper's scenes: the stepper moves on, not the story); the rest is still
 * returned (`rest`), for whoever waits it out instead.
 */
export function useStory(story, {
    pages = NO_PAGES,
    parts: otherParts = NO_PAGES,
    director = PART_LOADERS.director,
    reducedPage = null,
    rest = STORY_PLAYBACK.rest + (story.settles ?? 0),
    threshold = STORY_PLAYBACK.threshold,
    loop = STORY_PLAYBACK.loop,
    active = true,
} = {}) {
    const reduced = Boolean(useReducedMotion());
    // Every page the story shows (a beat may show another page than its unit's).
    const pageNames = useMemo(() => storyPages(story), [story]);

    // What the beats need, in the order they first need it.
    const needed = useMemo(() => {
        const order = [];
        story.beats.forEach((entry) => {
            const page = entry.page;
            [entry.needs, pages[page]?.part ? page : null].forEach((name) => {
                if (name && !order.includes(name)) order.push(name);
            });
        });

        return order;
    }, [story, pages]);

    // Mirrored from the playback (which needs the parts first): whether the story is in view.
    const [inView, setInView] = useState(false);
    // Where the story ends: its last beat's page (a beat may name its own).
    const restingPage = reduced ? (reducedPage ?? story.beats.at(-1)?.page ?? story.units.at(-1)?.page) : null;
    const loaderOf = (name) => otherParts[name] ?? pages[name]?.part ?? null;
    const cueNames = pageNames.filter((name) => pages[name]?.cues);

    const requests = [
        { name: 'director', loader: director, enabled: active && !reduced },
        ...cueNames.map((name) => ({ name: cuesPart(name), loader: pages[name].cues, enabled: active && !reduced })),
        ...needed.map((name, index) => ({
            name,
            loader: loaderOf(name),
            enabled: reduced
                ? restingPage === name
                : (loaded) => active && inView && (index === 0
                    ? Boolean(loaded.director) && cueNames.every((page) => loaded[cuesPart(page)])
                    : Boolean(loaded[needed[index - 1]])),
        })),
    ];
    const parts = useParts(requests);

    const ready = Boolean(parts.director) && cueNames.every((name) => parts[cuesPart(name)]);
    const enabled = useCallback((step) => {
        if (!active || !ready) return false;
        const next = story.beats[step];
        if (!next) return true;
        const page = next.page;

        return (!next.needs || Boolean(parts[next.needs])) && (!pages[page]?.part || Boolean(parts[page]));
    }, [active, ready, story, parts, pages]);

    const playback = usePlayback({ timeline: story.timeline, ...STORY_PLAYBACK, loop, rest, threshold, enabled });
    useEffect(() => {
        setInView(playback.inView);
    }, [playback.inView]);

    const at = useMemo(() => storyAt(story, playback.step), [story, playback.step]);
    const page = playback.reduced ? (reducedPage ?? at.page) : at.page;

    return { ...playback, story, at, page, parts, pages, pageNames, rest };
}

/**
 * One pass of the story, keyed by the playback cycle (a replay starts from
 * fresh markup): the pages, stacked, one showing at a time (a window
 * navigation dips: the page left fades out, 150ms; the next fades in with
 * an 8px rise, 250ms), and the director above them (the pointer and the
 * pages' cues), once its part has arrived.
 */
function Scene({ state, hero, viewportHeight }) {
    const { at, reduced, parts, page, story, pages, pageNames } = state;
    const layer = useRef(null);
    const track = useMotionControls();
    const visible = useDip(page, { reduced });
    const seen = useRef(new Set());
    // The pointer is the director's (a part); pages reach it through here.
    const cursorRef = useRef(null);
    const cursor = useMemo(() => ({
        moveTo: (target, options) => cursorRef.current?.moveTo(target, options) ?? 0,
        press: () => cursorRef.current?.press(),
    }), []);
    const Director = parts.director?.default;
    const cues = pageNames
        .filter((name) => parts[cuesPart(name)])
        .map((name) => ({ page: name, Cues: parts[cuesPart(name)].default }));

    const firstBeat = (name) => story.beats.findIndex((entry) => entry.page === name);

    return (
        <div ref={layer} data-story-layer="" className="relative overflow-hidden" style={{ height: viewportHeight }}>
            {pageNames.map((name) => {
                const Component = pages[name]?.Component ?? parts[name]?.default;
                const showing = visible(name);
                const first = firstBeat(name);
                const due = name === page || showing || seen.current.has(name) || (first !== -1 && at.step >= first - MOUNT_AHEAD);
                // A reduced-motion story shows its resting page and nothing else.
                if (!Component || !due || (reduced && name !== page)) return null;
                const wasSeen = seen.current.has(name);
                if (showing) seen.current.add(name);

                return (
                    <div
                        key={name}
                        data-story-page={name}
                        data-showing={showing ? '' : undefined}
                        className={cn(
                            'absolute inset-0 transition-[opacity,transform] ease-mk',
                            showing ? 'opacity-100' : 'opacity-0',
                            !showing && !wasSeen && !reduced && 'translate-y-2',
                            // Dropped at the swap (its fade's tail cut), or never shown.
                            visible.gone(name) && 'invisible',
                        )}
                        style={{
                            transitionDuration: `${showing ? PAGE_IN_MS : DIP_OUT_MS}ms`,
                            // Coming in a frame into its curve (never the plate alone).
                            transitionDelay: showing ? `-${DIP_IN_SKIP_MS}ms` : undefined,
                        }}
                    >
                        <Component
                            hero={hero}
                            at={at}
                            reduced={reduced}
                            cursor={cursor}
                            track={track}
                            parts={parts}
                            viewportHeight={viewportHeight}
                        />
                    </div>
                );
            })}
            {Director && !reduced && (
                <Director
                    layer={layer}
                    cursorRef={cursorRef}
                    track={track}
                    hero={hero}
                    at={at}
                    reduced={reduced}
                    viewportHeight={viewportHeight}
                    parts={parts}
                    cues={cues}
                />
            )}
        </div>
    );
}

/**
 * The story's window: sealed (VignetteScope, `label` for assistive
 * technology), a fixed-height app window (StoryWindow: `baseWidth`,
 * `height`, the pages' `trails`), and the scene, which dips back to its
 * opening at every replay (CycleRewind). `state` is useStory()'s; `hero`
 * the story's data, handed to every page and cue.
 */
export default function StoryVignette({ state, hero, trails = {}, baseWidth, height, label, className }) {
    const shownTrails = Object.fromEntries(state.pageNames.map((name) => [name, trails[name] ?? []]));

    return (
        <VignetteScope ref={state.ref} label={label} className={cn('h-full', className)}>
            <StoryWindow baseWidth={baseWidth} height={height} trails={shownTrails} page={state.page} reduced={state.reduced}>
                {(viewportHeight) => (
                    <CycleRewind cycle={state.cycle}>
                        <Scene key={state.cycle} state={state} hero={hero} viewportHeight={viewportHeight} />
                    </CycleRewind>
                )}
            </StoryWindow>
        </VignetteScope>
    );
}
